"use client";

import { Button } from "@/components/rac/button";
import { PREFERENCE_QUERY } from "@/gql/preference";
import { useBuyerStoreData } from "@/hooks/global-multiple";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@apollo/client";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

const BANNER_STORAGE_KEY = "preferences-banner-last-shown";
const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export function PreferencesBanner() {
	const [isVisible, setIsVisible] = useState(false);

	// Authentication and preferences data
	const { isAuthenticated } = useAuth();
	const buyerId = useBuyerStoreData(state => state.buyerId);

	// Query user preferences to check if they have any saved
	const { data: preferencesData, loading: isLoading } = useQuery(PREFERENCE_QUERY, {
		variables: {
			params: {
				getBuyerPreferences: {
					groupId: buyerId,
				},
			},
		},
		skip: !buyerId || !isAuthenticated,
	});

	// Check if user has preferences
	const hasPreferences = useMemo(() => {
		const preferences = preferencesData?.buyer?.getBuyerPreferences || [];
		return preferences.length > 0;
	}, [preferencesData]);

	useEffect(() => {
		try {
			const lastShown = localStorage.getItem(BANNER_STORAGE_KEY);
			const now = Date.now();

			// If never shown before, or more than 24 hours have passed
			if (!lastShown || now - parseInt(lastShown, 10) > ONE_DAY_MS) {
				setIsVisible(true);
				// Update the timestamp when we decide to show it
				localStorage.setItem(BANNER_STORAGE_KEY, now.toString());
			} else {
				setIsVisible(false);
			}
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			// If localStorage is not available, show the banner
			// console.warn("localStorage not available:", error);
			setIsVisible(true);
		}
	}, []);

	// Return null if user has preferences - don't show banner
	if (hasPreferences || isLoading || !isVisible) {
		return null;
	}

	const handleClose = () => {
		setIsVisible(false);
		// Update timestamp when manually closed to prevent showing again today
		try {
			localStorage.setItem(BANNER_STORAGE_KEY, Date.now().toString());
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			// console.warn("Could not save banner state:", error);
		}
	};

	return (
		<div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-200 relative z-10">
			<div className="max-w-7xl mx-auto px-2 py-1">
				<div className="flex items-center justify-center">
					<p className="text-sm lg:text-base text-center text-gray-800 leading-relaxed">
						<span className="font-medium">
							🎯 We have a novel way to help you avoid tedious manual searches and get accurate suggestions.
						</span>{" "}
						<Link href="/preference" className="text-blue-600 hover:text-blue-800 font-medium underline">
							Try now
						</Link>
					</p>
					<Button
						variant="ghost"
						size="icon"
						onClick={handleClose}
						className="flex-shrink-0 h-8 w-8 text-muted-foreground hover:text-foreground"
						aria-label="Close banner"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="m18 6-12 12" />
							<path d="m6 6 12 12" />
						</svg>
					</Button>
				</div>
			</div>
		</div>
	);
}
